import { isAfter } from 'date-fns';
import { get, noop } from 'lodash-es';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { setError } from '../../reducers/error';
import { setLoader } from '../../reducers/loader';
import {
	convertPosition,
	fetchDpDetails,
	fetchEdisSummary,
	fetchEdisUrl,
	fetchHoldings,
	fetchHoldingsV1,
	fetchInvestmentHistory,
	fetchPositions,
	fetchRealisedPnl,
	generateNewTpin,
	getHoldingsData,
	getPositions,
	getSelectedSquareoffIds,
	setEdisSummary,
	setHoldings,
	setHoldingsV1,
	setInvestmentHistory,
	setIsConvertPositionInProgress,
	setPositions,
	setRealisedPnl,
	setSelectedSquareoffIds,
	squareoffSelectedPositions,
} from '../../reducers/equity/stocksPortfolio';
import commonBaseService from '../../../services/commonBaseService';
import { resetActionError, setActionError } from '../../reducers/actionError';
import stocksPortfolioSagaService from '../../../services/equity/stocksPortfolio/stocksPortfolioSagaService';
import {
	setActionLoaderFalse,
	setActionLoaderTrue,
} from '../../reducers/actionLoader';
import {
	convert_position,
	fetch_dp_details,
	fetch_edis_summary,
	fetch_edis_url,
	fetch_holdings,
	fetch_holdings_v1,
	fetch_positions,
	fetch_realised_pnl,
	fetch_transaction_history,
	generate_new_tpin,
	place_bulk_order,
} from '../../../apis/equity/stocksPortfolio';

export default function* stocksPortfolioSaga() {
	yield takeLatest(fetchHoldings.toString(), handleFetchHoldings);
	yield takeLatest(fetchPositions.toString(), handleFetchPositions);
	yield takeLatest(
		fetchInvestmentHistory.toString(),
		handleFetchInvestmentHistory,
	);
	yield takeLatest(fetchRealisedPnl.toString(), handleFetchRealisedPnl);
	yield takeLatest(
		squareoffSelectedPositions.toString(),
		handleBulkSquareoff,
	);

	// EDIS
	yield takeLatest(fetchEdisSummary.toString(), handleFetchEdisSummary);
	yield takeLatest(fetchDpDetails.toString(), handleFetchDpDetails);
	yield takeLatest(fetchEdisUrl.toString(), handleFetchEdisUrl);
	yield takeLatest(generateNewTpin.toString(), handleGenerateNewTpin);
	yield takeLatest(fetchHoldingsV1.toString(), handleFetchHoldingsV1);
	yield takeLatest(convertPosition.toString(), handleConvertPosition);
}

function* handleConvertPosition({ payload = {} }) {
	const {params, Api, sagaCallback} = payload;
	try {
		yield put(setIsConvertPositionInProgress(true));
		const result = yield call(convert_position, params, Api);
		yield call(sagaCallback, result);
	} catch (e) {
		yield call(sagaCallback, { message: e.message });
	}
}

function* handleFetchHoldings({ payload = {} }) {
	const {
		isBgRefresh = false,
		data = {},
		Api,
		reconnectSocket = noop,
	} = payload;
	const holdingData = yield select(getHoldingsData);
	const expTime = get(holdingData, 'data.summary.exp_time', '');
	const isFetchedFailed = get(holdingData, 'isFetchFailed', false);
	try {
		const now = new Date();
		const expiryTime = new Date(expTime);
		const isExpiryTimeEmpty = commonBaseService.isEmptyOrUndefined(expTime);
		const isCacheExpired = isAfter(now, expiryTime);
		const isRefetchRequired =
			isCacheExpired ||
			isBgRefresh ||
			isExpiryTimeEmpty ||
			isFetchedFailed;
		if (isRefetchRequired) {
			if (isBgRefresh) {
				yield put(setHoldings({ isRefreshing: true }));
			} else {
				yield put(
					setHoldings({
						isLoading: true,
						isFetchFailed: false,
						errorMessage: '',
					}),
				);
			}
			const response = yield call(fetch_holdings, data, Api);
			const summary = get(response, 'holdings_info.summary', {});
			const holdings = get(response, 'holdings_info.holdings', {});
			const filteredHoldings =
				stocksPortfolioSagaService.filterHoldingsInvalidDataResponse(
					holdings,
				);
			yield put(
				setHoldings({
					data: {
						summary,
						holdings: filteredHoldings,
					},
				}),
			);
		}
	} catch (err) {
		yield put(
			setHoldings({ isFetchFailed: true, errorMessage: err?.message }),
		);
	} finally {
		yield call(reconnectSocket);
		yield put(setHoldings({ isLoading: false, isRefreshing: false }));
	}
}

function* handleFetchInvestmentHistory({ payload = {} }) {
	const { data = {}, Api } = payload;
	try {
		yield put(
			setInvestmentHistory({
				isLoading: true,
				isFetchFailed: false,
				errorMessage: '',
			}),
		);
		const response = yield call(fetch_transaction_history, data, Api);
		const formattedData =
			stocksPortfolioSagaService.formatInvestmentHistoryData(response);
		yield put(setInvestmentHistory({ data: formattedData }));
	} catch (err) {
		console.log('error in holdingsList', err.message);
		yield put(
			setInvestmentHistory({
				isFetchFailed: true,
				errorMessage: err.message,
			}),
		);
	} finally {
		yield put(setInvestmentHistory({ isLoading: false }));
	}
}

function* handleFetchRealisedPnl({ payload = {} }) {
	const {
		sagaCallback = noop,
		token = '',
		data = {},
		type = '',
		Api,
	} = payload;
	try {
		yield put(setRealisedPnl({ type, payload: { isLoading: true } }));
		yield put(
			setRealisedPnl({
				type,
				payload: { data: {}, isError: false, message: '' },
			}),
		);
		const response = yield call(fetch_realised_pnl, data, Api, token);

		const result = get(response, 'result', []);
		const summary = get(response, 'summary', {});
		const transactions = get(response, 'transactions_list', []);

		const statement = {
			result,
			summary,
			transactions,
		};

		yield put(
			setRealisedPnl({
				payload: { data: statement },
				type,
			}),
		);

		yield call(sagaCallback, statement);
	} catch (err) {
		console.log('error in Realised PnL', err.message);
		yield put(
			setRealisedPnl({
				type,
				payload: { isError: true, message: err.message },
			}),
		);
	} finally {
		yield put(setRealisedPnl({ type, payload: { isLoading: false } }));
	}
}

function* handleFetchPositions({ payload }) {
	const { isBgRefresh = false, Api, reconnectSocket = noop } = payload;
	try {
		if (isBgRefresh) {
			yield put(setPositions({ isRefreshing: true }));
		} else {
			yield put(
				setPositions({
					isLoading: true,
					isFetchFailed: false,
					errorMessage: '',
				}),
			);
		}
		const response = yield call(fetch_positions, payload, Api);
		const summary = get(response, 'positions_info.summary', {});
		const positions = get(response, 'positions_info.positions', []);
		const filteredPositions =
			stocksPortfolioSagaService.filterPositionsInvalidDataResponse(
				positions,
			);
		const positionsObj =
			stocksPortfolioSagaService.formatPositionsObject(filteredPositions);
		yield put(
			setPositions({
				data: {
					summary,
					positionsList: filteredPositions,
					positions: positionsObj,
				},
			}),
		);
		yield put(setSelectedSquareoffIds([]));
	} catch (err) {
		console.log('error in fetch positions', err.message);
		yield put(
			setPositions({ isFetchFailed: true, errorMessage: err?.message }),
		);
	} finally {
		yield call(reconnectSocket);
		yield put(setPositions({ isLoading: false, isRefreshing: false }));
	}
}

function* handleBulkSquareoff({ payload }) {
	const { appVersion, OdinApi } = payload;
	try {
		const selectedSquareoffIds = yield select(getSelectedSquareoffIds);
		const { data = {} } = yield select(getPositions);
		yield put(setActionLoaderTrue(squareoffSelectedPositions));
		yield put(resetActionError(squareoffSelectedPositions));
		const positionsData = get(data, 'positions', {});
		const formatedBulksquareoffPayload = yield call(
			stocksPortfolioSagaService.formateBulkSquareoffPayload,
			selectedSquareoffIds,
			positionsData,
			appVersion,
		);
		yield call(place_bulk_order, formatedBulksquareoffPayload, OdinApi);
	} catch (err) {
		console.log('bulk square off error', err.message);
		yield put(
			setActionError({
				actionName: squareoffSelectedPositions,
				isFetchFailed: true,
				message: err.message,
			}),
		);
	} finally {
		yield put(setActionLoaderFalse(squareoffSelectedPositions));
	}
}

// EDIS

function* handleFetchEdisSummary({ payload = {} }) {
	const {
		sagaCallback = noop,
		errorCallback = noop,
		screen = '',
		Api,
	} = payload;
	try {
		yield put(setActionLoaderTrue(fetchEdisSummary));
		yield put(setError({ screen, isFetchFailed: false, message: '' }));
		const response = yield call(fetch_edis_summary, Api);

		const { DPQtyTable } = response;
		yield put(setEdisSummary(DPQtyTable));
		yield call(sagaCallback, DPQtyTable);
	} catch (err) {
		console.log('error in edis summary', err.message);
		yield call(errorCallback, err.message);
		yield put(
			setError({ isFetchFailed: true, screen, message: err.message }),
		);
	} finally {
		yield put(setActionLoaderFalse(fetchEdisSummary));
	}
}

function* handleFetchDpDetails({ payload = {} }) {
	const { sagaCallback = noop, screen = '', Api } = payload;
	try {
		yield put(setLoader({ screen, isPageLoading: true }));
		yield put(setError({ screen, isFetchFailed: false, message: '' }));
		const response = yield call(fetch_dp_details, Api);
		yield call(sagaCallback, response);
	} catch (err) {
		console.log('error in fetching dp details', err.message);
		yield put(
			setError({ isFetchFailed: true, screen, message: err.message }),
		);
	} finally {
		yield put(setLoader({ screen, isPageLoading: false }));
	}
}

function* handleFetchEdisUrl({ payload = {} }) {
	const { sagaCallback = noop, screen = '', params = {}, Api } = payload;

	try {
		yield put(setLoader({ screen, isButtonLoading: true }));
		yield put(setError({ screen, isFetchFailed: false, message: '' }));
		const response = yield call(fetch_edis_url, params, Api);
		yield call(sagaCallback, response);
	} catch (err) {
		console.log('error in edis url', err.message);
		yield put(
			setError({ isFetchFailed: true, screen, message: err.message }),
		);
	} finally {
		yield put(setLoader({ screen, isButtonLoading: false }));
	}
}

function* handleGenerateNewTpin({ payload = {} }) {
	const { sagaCallback = noop, screen = '', Api } = payload;

	try {
		yield put(setActionLoaderTrue(generateNewTpin));
		yield put(setError({ screen, isFetchFailed: false, message: '' }));
		const response = yield call(generate_new_tpin, Api);
		yield call(sagaCallback, response);
	} catch (err) {
		console.log('error in generate tpin', err.message);
		yield put(
			setError({ isFetchFailed: true, screen, message: err.message }),
		);
		yield call(sagaCallback, { message: err.message });
	} finally {
		yield put(setActionLoaderFalse(generateNewTpin));
	}
}

function* handleFetchHoldingsV1({ payload = {} }) {
	const { sagaCallback = noop, screen = '', Api } = payload;

	try {
		yield put(setActionLoaderTrue(fetchHoldingsV1));
		yield put(setError({ screen, isFetchFailed: false, message: '' }));
		const response = yield call(fetch_holdings_v1, Api);
		yield put(setHoldingsV1(response?.data));
		yield call(sagaCallback, response?.data);
	} catch (err) {
		console.log('error in fetch holdings v1', err.message);
		yield put(
			setError({ isFetchFailed: true, screen, message: err.message }),
		);
		yield call(sagaCallback, { message: err.message, isFailed: true });
	} finally {
		yield put(setActionLoaderFalse(fetchHoldingsV1));
	}
}
